import FormModal from '@/components/common/Modal/FormModal';
import MultiSelectDropdown from '@/components/common/MultiSelectDropdown/MultiSelectDropdown';
import { SimpleCheckbox } from '@/components/common/SimpleCheckbox/SimpleCheckbox';
import { useErrorContext } from '@/context/ErrorContext';
import { IOptions } from '@/data/models/option';
import { ITrack } from '@/data/models/track';
import { isDefined } from '@/helpers/util';
import useGetAlbumQuery from '@/hooks/useGetAlbumQuery';
import useUpdateProductMutation from '@/hooks/useUpdateProductMutation';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CopyProductMetadataToTracksModal.module.scss';
import {
  copyProductMetadataToTracks,
  MetadataField,
} from './copyProductMetadataToTracks';
import { ProductViewModes } from '@/utils/productStateUtils';
import useGetActiveChangeRequestQuery from '@/hooks/useGetActiveChangeRequestQuery';
import { IChangeRequest } from '@/data/models/changeRequest';
import { IAlbum } from '@/data/models/album';
import useUpdateChangeRequestMutation from '@/hooks/useUpdateChangeRequestMutation';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';

interface Props {
  show: boolean;
  onClose: () => void;
  albumId: string;
  selectedTracks: ITrack[];
  allTracks: ITrack[];
  reloadTracks: () => Promise<void>;
  productViewMode?: ProductViewModes;
}

export default function CopyProductMetadataToTracksModal({
  show,
  onClose,
  albumId,
  selectedTracks,
  allTracks,
  reloadTracks,
  productViewMode,
}: Props) {
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const isChangeRequest = productViewMode === ProductViewModes.ChangeRequest;
  const [isPending, setIsPending] = useState(false);
  const [selectedFields, setSelectedFields] = useState<IOptions[]>([]);
  const [overwriteValues, setOverwriteValues] = useState(false);

  const { data: album } = !isChangeRequest
    ? useGetAlbumQuery(albumId)
    : useGetActiveChangeRequestQuery(albumId);
  const updateProductMutation = useUpdateProductMutation();
  const updateChangeRequestMutation = useUpdateChangeRequestMutation();

  const metadataOptions: IOptions[] = useMemo(
    () => [
      { label: t('common.grid.selectAll'), value: 'selectAll' },
      { label: t('product.editProduct.title'), value: 'title' },
      { label: t('product.editProduct.version'), value: 'version' },
      {
        label: t('product.copyProductMetadata.fields.artists'),
        value: 'artists',
      },
      { label: t('product.editProduct.genre'), value: 'genre' },
      { label: t('product.editProduct.cline'), value: 'cLine' },
      { label: t('product.editProduct.pline'), value: 'pLine' },
    ],
    [t]
  );

  const handleSubmit = async () => {
    if (!album) return;
    const baseAlbum: IAlbum = !isChangeRequest
      ? (album as IAlbum)
      : ((album as IChangeRequest)?.changeRequestAlbum as IAlbum);
    setIsPending(true);

    try {
      const updatedAlbum = copyProductMetadataToTracks({
        album: baseAlbum,
        allTracks,
        selectedTrackIds: selectedTracks
          .map((track) => track.id)
          .filter(isDefined),
        selectedFields: selectedFields
          .map((field) => field.value)
          .filter((value): value is MetadataField => value !== 'selectAll'),
        overwriteValues,
      });

      if (isChangeRequest) {
        const changeRequest = album as IChangeRequest;
        await updateChangeRequestMutation.mutateAsync({
          albumId,
          album: updatedAlbum,
          changeRequest,
          state: updatedAlbum.albumState,
        });
      } else {
        await updateProductMutation.mutateAsync({
          albumId,
          album: updatedAlbum,
          state: updatedAlbum.albumState,
        });
      }

      await reloadTracks();
      onClose();
    } catch (error) {
      setError(getSaveErrorMessage(error, isChangeRequest));
      onClose();
    } finally {
      setIsPending(false);
    }
  };

  if (!show) return null;

  return (
    <FormModal
      show={show}
      onClose={onClose}
      onSubmit={handleSubmit}
      titleText={t('product.copyProductMetadata.title')}
      submitButtonText={t('common.grid.copy')}
      submitButtonDisabled={isPending || selectedFields.length === 0}
    >
      <div>
        <p>{t('product.copyProductMetadata.description')}</p>
        <MultiSelectDropdown
          options={metadataOptions}
          selectedOptions={selectedFields}
          onChange={setSelectedFields}
        />
        <div className={styles.overwriteContainer}>
          <SimpleCheckbox
            checked={overwriteValues}
            onChange={(e) => setOverwriteValues(e.target.checked)}
            text={t('product.copyProductMetadata.overwriteExisting')}
          />
        </div>
      </div>
    </FormModal>
  );
}
